import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Typography,
  Table,
  Input,
  Space,
  Button,
  App,
  Row,
  Col,
  DatePicker,
} from 'antd';
import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { listUserActivities } from '../../services/user-activity';
import { Tooltip } from 'antd';


const { Title } = Typography;
const { useApp } = App;

const UserActivitiesList = () => {
  const { message } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  const getDefaultFilters = () => ({
    search: '', // nombre, apellido, correo
    dateRange: null, // [dayjs, dayjs] o null
    activity: '', // url
  });

  const [filters, setFilters] = useState(getDefaultFilters);
  const fetchData = useCallback(async (page, pageSize, srt, currentFilters) => {
//   const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const buildParams = (f, pg, pgSize) => ({
        search: f?.search || undefined,
        from: f?.dateRange?.[0]?.format('YYYY-MM-DD') ?? undefined,
        to: f?.dateRange?.[1]?.format('YYYY-MM-DD') ?? undefined,
        activity: f?.activity?.trim() || undefined,
        page: pg || pagination.current,
        pageSize: pgSize || pagination.pageSize,
        // search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      const params = buildParams(currentFilters, page, pageSize);
    //   const { items, total } = await listUserActivities({
    //     page: page || pagination.current,
    //     pageSize: pageSize || pagination.pageSize,
    //     search: q || undefined,
    //     sortField: srt?.field,
    //     sortOrder: srt?.order,
    //   });
    const { items, total } = await listUserActivities(params);
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total,
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de actividades de los usuarios.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filtersTable, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearch = () => {
    fetchData(1, pagination.pageSize, sorter, filters);
  };

  const handleClearFilters = () => {
    const reset = getDefaultFilters();
    setFilters(reset);
    // Recargar inmediatamente con filtros reseteados
    fetchData(1, pagination.pageSize, sorter, reset);
  };

  const columns = [
    { title: 'Fecha', dataIndex: 'created_at', key: 'created_at', sorter: true, render: (date: string) => {
        if (!date) return '';
            const d = new Date(date);
            return d.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            });
        }, 
    },
    { title: 'Nombre de Usuario', dataIndex: 'user', key: 'user', render: (user) => (`${user?.firstName} ${user?.lastName} ${user?.secondLastName ?? ''}`) || '-', },
    { title: 'Correo electrónico', dataIndex: 'user', key: 'user', sorter: true, render: (user) => user?.email || '-', },
    { title: 'Dirección IP', dataIndex: 'ip_address', key: 'ip_addes', sorter: true },
    { title: 'Método', dataIndex: 'method', key: 'method', sorter: true },
    { title: 'Actividad', dataIndex: 'url', key: 'url', sorter: true, render: (text: string, record) => {
        if (!text) return '-'; // manejar valores vacíos
            return (
            <a
                href={text}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff' }}
            >
                {text}
            </a>
            );
        },
    },
    { title: 'Descripción', dataIndex: 'description', key: 'description', sorter: true },
  ];
  

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>Actividad de los Usuarios</Title>
      </div>
      <Card
          type="inner"
          title="Filtros"
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[16, 16]}>

            <Col xs={24} sm={6} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha Inicio</span>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filters.dateRange?.[0] || null}
                  onChange={(date) => {
                    if (date) {
                      const newRange = filters.dateRange
                        ? [date, filters.dateRange[1]]
                        : [date, null];
                      handleFilterChange('dateRange', newRange);
                    } else {
                      // Si se limpia la fecha inicio, mantener fin si existe
                      const newRange = filters.dateRange?.[1]
                        ? [null, filters.dateRange[1]]
                        : null;
                      handleFilterChange('dateRange', newRange);
                    }
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} sm={6} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Fecha Fin</span>
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={filters.dateRange?.[1] || null}
                  onChange={(date) => {
                    if (date) {
                      const newRange = filters.dateRange
                        ? [filters.dateRange[0], date]
                        : [null, date];
                      handleFilterChange('dateRange', newRange);
                    } else {
                      // Si se limpia la fecha fin, mantener inicio si existe
                      const newRange = filters.dateRange?.[0]
                        ? [filters.dateRange[0], null]
                        : null;
                      handleFilterChange('dateRange', newRange);
                    }
                  }}
                  disabledDate={(current) => {
                    // Deshabilitar fechas anteriores a la fecha inicio si existe
                    if (filters.dateRange?.[0]) {
                      return current && current < filters.dateRange[0].startOf('day');
                    }
                    return false;
                  }}
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Buscar</span>
                <Input
                  placeholder="nombre, apellido, correo"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  allowClear
                />
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 500 }}>Actividad</span>
                <Input
                  placeholder="Usuarios, actividades"
                  value={filters.activity}
                  onChange={(e) => handleFilterChange('activity', e.target.value)}
                  allowClear
                />
              </Space>
            </Col>

          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Space>
              <Tooltip title="Buscar">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                  shape="circle"
                  size="large"
                />
              </Tooltip>
              <Tooltip title="limpiar filtros">
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClearFilters}
                  shape="circle"
                  size="large"
                />
              </Tooltip>
            </Space>
          </Row>
        </Card>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* <Input.Search
            placeholder="Buscar por nombre o email"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          /> */}
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} actividades`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default UserActivitiesList;
